import React, { useEffect, useState } from 'react';
import { Select, Spin, message } from 'antd';
import debounce from 'lodash/debounce';
import axios from 'axios';
import { Avatar } from '@mui/material';
import { apiGetAllCustomer } from '@services/ManageCustomers/CustomerServices';

const { Option } = Select;

const CustomerSelect = ({ onChange, initValue, disabled, onDefaultSet, status = undefined }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customerSelected, setCustomerSelected] = useState(null);

  const getCustomerOption = (customer) => ({
    value: customer.code,
    label: `${customer.code} - ${customer.name}`,
    code: customer.code
  });

  const fetchCustomers = async (searchText) => {
    setLoading(true);
    try {
      const response = await apiGetAllCustomer({
        keyWord: searchText ?? undefined,
        page: 1,
        size: 200,
        status: status ?? undefined
      });
      const customerData = response.data?.data || [];
      setCustomers(customerData);

      // Set default to first customer if not already set and onDefaultSet callback exists
      if (customerData.length > 0 && !customerSelected && !initValue && onDefaultSet) {
        const firstCustomer = customerData[0];
        const defaultValue = getCustomerOption(firstCustomer);
        setCustomerSelected(defaultValue);
        onDefaultSet(firstCustomer.code, defaultValue);
      }
    } catch (error) {
      message.error('Có lỗi khi lấy thông tin khách hàng!');
      console.error('Failed to fetch customers', error);
    }
    setLoading(false);
  };

  const handleSearch = debounce((value) => {
    if (value) {
      fetchCustomers(value);
    } else {
      setCustomers([]);
    }
  }, 300);

  const handleChange = (value, option) => {
    setCustomerSelected(value);
    onChange && onChange(value, { ...option, code: value.value });
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (!initValue) {
      setCustomerSelected(null);
      return;
    }

    const customerFound = customers.find((customer) => customer.code === initValue);
    if (customerFound) {
      setCustomerSelected(getCustomerOption(customerFound));
    }
  }, [initValue, customers]);

  useEffect(() => {
    const fetchInitCustomer = async () => {
      if (!initValue) {
        return;
      }

      const customerFound = customers.find((customer) => customer.code === initValue);
      if (customerFound) {
        return;
      }

      setLoading(true);
      try {
        const response = await apiGetAllCustomer({
          keyWord: initValue,
          page: 1,
          size: 10,
          status: status ?? undefined
        });
        const customerData = response.data?.data || [];
        const selectedCustomer = customerData.find((customer) => customer.code === initValue);

        if (selectedCustomer) {
          setCustomers((prev) => {
            const merged = [selectedCustomer, ...prev];
            return Array.from(
              new Map(merged.map((customer) => [customer.code, customer])).values()
            );
          });
          setCustomerSelected(getCustomerOption(selectedCustomer));
        }
      } catch (error) {
        message.error('CÃ³ lá»—i khi láº¥y thÃ´ng tin khÃ¡ch hÃ ng!');
        console.error('Failed to fetch init customer', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitCustomer();
  }, [initValue, status]);

  const renderCustomerOption = (customer) => (
    <Option key={customer.code} value={customer.code} label={`${customer.code} - ${customer.name}`}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Avatar
          alt={customer?.name?.toUpperCase()}
          src={customer.avatar ? customer?.avatar : 'https://'}
          sx={{ bgcolor: '#10b981' }}
          style={{ marginRight: 8 }}
        />
        <div>
          <div style={{ fontSize: '14px', fontWeight: '400', color: '#0284c7' }}>
            {`${customer.code} - ${customer.name}`}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', fontWeight: '400' }}>
            {customer.address}
          </div>
        </div>
      </div>
    </Option>
  );

  return (
    <Select
      disabled={disabled}
      loading={loading}
      showSearch
      labelInValue
      placeholder="Lựa chọn khách hàng"
      onChange={handleChange}
      value={customerSelected}
      notFoundContent={loading ? <Spin size="small" /> : null}
      filterOption={false}
      onSearch={handleSearch}
      style={{ width: '100%', height: '36px' }}
      optionLabelProp="label">
      {customers.map(renderCustomerOption)}
    </Select>
  );
};

export default CustomerSelect;
